import React, { useEffect, useState } from 'react'
import { TabGroup, TabItem } from "../settings-v2/receipt-printing"
import { EligibleItemsContainer, SettingsActionButtonContainer, SettingsActionButtonWrapper, SettingsFormGroup, SettingsFormGroupInput, SettingsFormInputContainer, SettingsFormLabelContainer, SettingsFormLabelLeftContainer, SettingsFormLabelRightContainer } from '../settings-v2'
import { IconHelp } from '../../../../components/v2/icons'
import { FormTreeSelect, RestaurantIconSelect, RotateLoader } from '@lib/components'
import { CookieColorPickerContent, CookieColorPickerWrapper, CookieColorsContainer, CookieColorsRow } from '../settings-v2/policies-agreements'
import { ColorPicker } from '@lib/components/src'
import { RestaurantUtils } from '@lib/common'
import { useStores } from '../../../../../mobx/useStores'
import { RestaurantForm } from '../../../../../mobx/components/restaurant-form'
import { FastField } from 'formik'
import cloneDeep from "lodash/cloneDeep";
import shortid from "shortid";
import { UI } from '../../../../../core/ui';

type FormValues = T.Schema.Restaurant.Menu.RestaurantDishTag;

interface Props {
    selectedTagIndex?: number
}

export const ItemTags = (props: Props) => {
    const { selectedTagIndex } = props
    const [tab ,setTab] = useState('general')
    const [iconType, setIconType] = useState('text')
    const [add_to, setAddTo] = useState<string[]>([])
    const [selectedIndex, setSelectedIndex] = useState(selectedTagIndex)

    const store = useStores()
    const restaurant = store.restaurant!
    const { activeMobileMenu, setActiveMobileMenu } = store
    const isV2 = store.isNewMenuSchema;

    useEffect(() => {
        setSelectedIndex(selectedTagIndex)
    }, [selectedTagIndex])

    useEffect(() => {
        if (isV2 && restaurant.dish_tags?.length === 0) {
            store.api.tags_list(restaurant._id!).then((res: any) => {
                if (res?.tags && Array.isArray(res.tags)) {
                    const tags = (res.tags as any[]).map((t: any) => ({
                        _id: t.id ?? t._id,
                        name: t.name ?? '',
                        type: t.type ?? 'none',
                        text: t.text ?? '',
                        icon: t.icon ?? '',
                        color: t.color ?? { icon: { background: '#fff', text: '#ff7d4b' }, wrapper: { background: '#ff7d4b', text: '#fff' } },
                    }));
                    store.updateRestaurant({ dish_tags: tags });
                }
            }).catch(() => {});
        }
    }, [isV2, restaurant._id]);

    let initialValues: FormValues;
    if (selectedIndex != null && selectedIndex > -1 && restaurant.dish_tags?.[selectedIndex]) {
        const item = restaurant.dish_tags[selectedIndex];
        initialValues = cloneDeep(item) as FormValues;
    } else {
        initialValues = {
            _id: shortid.generate(),
            name: "",
            type: "none",
            text: "Example",
            icon: "",
            color: {
                icon: { background: "#fff", text: "#ff7d4b" },
                wrapper: { background: "#ff7d4b", text: "#fff" },
            },
        };
    }

    const addToFromV2Items = (tagId: string): string[] => {
        const menus = (restaurant as any).menus_v2 || [];
        const ids: string[] = [];
        for (const menu of menus) {
            for (const item of menu.items || []) {
                const tagIds = (item as any).tag_ids || [];
                if (tagIds.includes(tagId)) ids.push(item.id);
            }
        }
        return ids;
    };

    useEffect(() => {
        if (isV2 && selectedIndex != null && selectedIndex > -1 && restaurant.dish_tags?.[selectedIndex]) {
            const tagId = (restaurant.dish_tags[selectedIndex] as any).id ?? restaurant.dish_tags[selectedIndex]._id;
            setAddTo(addToFromV2Items(tagId));
        }
    }, [isV2, selectedIndex, restaurant.dish_tags]);

    const create_item = (): T.Schema.Restaurant.Menu.RestaurantDishTag => ({
        _id: shortid.generate(),
        name: "",
        type: "none",
        text: "Example",
        icon: "",
        color: {
            icon: { background: "#fff", text: "#ff7d4b" },
            wrapper: { background: "#ff7d4b", text: "#fff" },
        },
    });

    const submitV1 = async (r: T.Schema.Restaurant.RestaurantSchema, values: FormValues) => {
        const existing = r.dish_tags.findIndex((p) => p._id === values._id);
        if (existing === -1) r.dish_tags.push(values);
        else r.dish_tags[existing] = values;
        for (const [i, m] of r.menus.entries()) {
            for (const [k, c] of m.categories.entries()) {
                for (const [z, d] of c.dishes.entries()) {
                    if (add_to.indexOf(d._id) === -1) {
                        const index = d.tags.indexOf(values._id);
                        if (index !== -1) r.menus[i].categories[k].dishes[z].tags.splice(index, 1);
                    } else {
                        const index = d.tags.indexOf(values._id);
                        if (index === -1) r.menus[i].categories[k].dishes[z].tags.push(values._id);
                    }
                }
            }
        }
        return { r, update: { $set: { menus: r.menus, dish_tags: r.dish_tags } } };
    };

    const customSubmitV2 = async (values: FormValues, form: any) => {
        form.setSubmitting(true);
        const tagId = (values as any).id ?? values._id;
        const isEdit = selectedIndex != null && selectedIndex > -1;
        const payload = {
            name: values.name,
            type: values.type,
            text: values.text,
            icon: values.icon,
            color: values.color,
        };

        // if(store.isNewMenuSchema) {
        //     //@ts-ignore
        //     payload._internal = {
        //         color: values.color,
        //         type: values.type,
        //         icon: values.icon
        //     }
        // }
        let targetTagId = tagId;
        try {
            if (isEdit) {
                await store.api.tags_update({ restaurant_id: restaurant._id!, tag_id: tagId, data: payload });
            } else {
                const created = await store.api.tags_create({ restaurant_id: restaurant._id!, data: { ...payload } });
                const newId = (created as any)?.tag?.id ?? (created as any)?.id ?? (created as any)?.data?.id;
                if (newId) {
                    targetTagId = newId;
                    (values as any)._id = newId;
                }
            }
            const menusV2 = cloneDeep((restaurant as any).menus_v2 || []) as any[];
            const itemsToUpdate: { menu_id: string; category_id: string; id: string; tags: string[] }[] = [];
            for (const menu of menusV2) {
                for (const item of menu.items || []) {
                    const tagIds = item.tag_ids || [];
                    const hasTag = tagIds.includes(targetTagId);
                    const shouldHave = add_to.includes(item.id);
                    if (hasTag !== shouldHave) {
                        const next = shouldHave
                            ? [...tagIds, targetTagId]
                            : tagIds.filter((id: string) => id !== targetTagId);
                        itemsToUpdate.push({
                            menu_id: menu.id,
                            category_id: item.category_id,
                            id: item.id,
                            tags: next,
                        })
                        item.tag_ids = next;
                    }
                }
            }
            if (itemsToUpdate.length > 0) {
                await store.api.menu_partial_update_batch_items({
                    restaurant_id: restaurant._id!,
                    data: { items: itemsToUpdate },
                });
            }
            store.updateRestaurant({ menus_v2: menusV2 });
            if (isEdit) {
                const idx = restaurant.dish_tags?.findIndex((p) => (p as any)._id === tagId || (p as any).id === tagId);
                if (idx !== undefined && idx >= 0 && restaurant.dish_tags) {
                    const next = [...restaurant.dish_tags];
                    next[idx] = { ...values, _id: tagId };
                    store.updateRestaurant({ dish_tags: next });
                }
            } else {
                const newTag = { ...values, _id: (values as any).id ?? values._id };
                store.updateRestaurant({ dish_tags: [...(restaurant.dish_tags || []), newTag] });

            }
            UI.notification.success(isEdit ? 'Tag edited' : 'Tag created');
            setSelectedIndex(-1);
            setActiveMobileMenu(activeMobileMenu.referrer?.pop()!, Array.from(new Set(activeMobileMenu.referrer || [])));
        } catch (e: any) {
            UI.notification.error(e?.message ?? 'Failed to save tag');
        }
        form.setSubmitting(false);
    };

    return (<div style={{background: '#fefefe', paddingBottom: 81}}>
        <TabGroup className='menus options-set-header options-set-header-2'>
            <TabItem
                active={tab == 'general'}
                onClick={() => setTab('general')}>General</TabItem>
            <TabItem
                active={tab == 'add'}
                onClick={() => setTab('add')}>Add / Remove from Items</TabItem>
        </TabGroup>
        <RestaurantForm<FormValues>
                    submit={async (r, values) => {

                        const existing = r.dish_tags.findIndex((p) => p._id === values._id);

                        if (existing === -1) {
                            r.dish_tags.push(values);
                        }
                        else {
                            r.dish_tags[existing] = values;
                        }

                        for (const [i, m] of r.menus.entries()) {
                            for (const [k, c] of m.categories.entries()) {
                                for (const [z, d] of c.dishes.entries()) {
                                    if (add_to.indexOf(d._id) === -1) {
                                        const index = d.tags.indexOf(values._id);
                                        if (index !== -1) {
                                            r.menus[i].categories[k].dishes[z].tags.splice(index, 1);
                                        }
                                    }
                                    else {
                                        const index = d.tags.indexOf(values._id);
                                        if (index === -1) {
                                            r.menus[i].categories[k].dishes[z].tags.push(values._id);
                                        }
                                    }
                                }
                            }
                        }

                        const update = {
                            $set: {
                                menus: r.menus,
                                dish_tags: r.dish_tags,
                            },
                        };

                        return { r, update };
                    }}
                    customSubmit={isV2 ? customSubmitV2 : undefined}
					validators={{
						name: (values) => {
							if (!values.name)
								return { name: "This field is required" };
						},
					}}
					initialValues={initialValues}
					onSuccess={() => {
                        setSelectedIndex(-1)
                        store.setActiveMobileMenu(
                            store.activeMobileMenu.referrer.pop()!,
                            Array.from(new Set(store.activeMobileMenu.referrer))
                        )
                    }}
					onSuccessMessage={activeMobileMenu.name === "edit-dish-tag" ? "Tag edited" : "Tag created"}
                    width='100%'
				>
                    {({ form, error, getFieldError }) => {
                    const { isSubmitting, setFieldValue, setFieldTouched, setFieldError, submitCount, values } = form;
                    return (<>
                        <SettingsFormGroup>
                            {tab == 'general' && <>
                            <SettingsFormGroupInput
                                style={{
                                    paddingBottom: 16,
                                    borderBottom: '1px dashed #dfdfdf'
                                }}>
                                <SettingsFormLabelContainer>
                                    <SettingsFormLabelLeftContainer>Name</SettingsFormLabelLeftContainer>
                                    <SettingsFormLabelRightContainer><IconHelp /></SettingsFormLabelRightContainer>
                                </SettingsFormLabelContainer>
                                <SettingsFormInputContainer>
                                    <FastField
                                        name="name"
                                        render={({ field }: any) => (
                                        <input {...field} required />
                                     )}/>
                                </SettingsFormInputContainer>
                            </SettingsFormGroupInput>
                            <SettingsFormGroupInput
                                style={{
                                    paddingBottom: 16,
                                    borderBottom: '1px dashed #dfdfdf'
                                }}>
                                <SettingsFormLabelContainer>
                                    <SettingsFormLabelLeftContainer>Tag Text</SettingsFormLabelLeftContainer>
                                    <SettingsFormLabelRightContainer><IconHelp /></SettingsFormLabelRightContainer>
                                </SettingsFormLabelContainer>
                                <SettingsFormInputContainer>
                                    <FastField
                                        name="text"
                                        render={({ field }: any) => (
                                        <input {...field} />
                                     )}/>
                                </SettingsFormInputContainer>
                            </SettingsFormGroupInput>
                            <SettingsFormGroupInput>
                                <SettingsFormLabelContainer>
                                    <SettingsFormLabelLeftContainer>Icon Type</SettingsFormLabelLeftContainer>
                                    <SettingsFormLabelRightContainer><IconHelp /></SettingsFormLabelRightContainer>
                                </SettingsFormLabelContainer>
                                <FastField
                                    name="type"
                                    render={({ field }: any) => (
                                    <TabGroup style={{
                                        padding: '0 0 16px',
                                        borderBottom: '1px dashed #dfdfdf',
                                        overflow: 'unset'
                                    }}>
                                        <TabItem
                                            active={iconType == 'text'}
                                            onClick={() => {
                                                setIconType('text')
                                                setFieldValue("type", 'text')
												setFieldValue("icon", "")
                                            }}
                                            className="flex-unset-desktop w-108">Text</TabItem>
                                        <TabItem
                                            active={iconType == 'icon'}
                                            onClick={() => {
                                                setIconType('icon')
                                                setFieldValue("type", 'icon')
												setFieldValue("icon", "")
                                            }}
                                            className="flex-unset-desktop w-108">Icon</TabItem>
                                        <TabItem
                                            active={iconType == 'none'}
                                            onClick={() => {
                                                setIconType('none')
                                                setFieldValue("type", 'none')
												setFieldValue("icon", "")
                                            }}
                                            className="flex-unset-desktop w-108">None</TabItem>
                                    </TabGroup>
                                )} />
                            </SettingsFormGroupInput>
                            {iconType == 'text' && <SettingsFormGroupInput
                                style={{
                                    paddingBottom: 16,
                                    borderBottom: '1px dashed #dfdfdf'
                                }}>
                                <SettingsFormLabelContainer>
                                    <SettingsFormLabelLeftContainer>Icon Text</SettingsFormLabelLeftContainer>
                                    <SettingsFormLabelRightContainer><IconHelp /></SettingsFormLabelRightContainer>
                                </SettingsFormLabelContainer>
                                <SettingsFormInputContainer>
                                    <FastField
                                        name="icon"
                                        render={({ field }: any) => (
                                        <input {...field} />
                                     )}/>
                                </SettingsFormInputContainer>
                            </SettingsFormGroupInput> }
                            {iconType == 'icon' && <SettingsFormGroupInput
                                style={{
                                    paddingBottom: 16,
                                    borderBottom: '1px dashed #dfdfdf'
                                }}>
                                <SettingsFormLabelContainer>
                                    <SettingsFormLabelLeftContainer>Select an Icon</SettingsFormLabelLeftContainer>
                                    <SettingsFormLabelRightContainer><IconHelp /></SettingsFormLabelRightContainer>
                                </SettingsFormLabelContainer>
                                <FastField
                                    name="icon"
                                    render={({ field }: any) => (
                                    <RestaurantIconSelect
                                        selected={field.value}
										onChange={(icon) => setFieldValue("icon", icon)} />
                                )} />
                            </SettingsFormGroupInput> }

                            <SettingsFormGroupInput style={{gap: 16, borderBottom: '1px dashed var(--Secondary-Light-Grey-1, #DFDFDF)', paddingBottom: 16}}>
                                <SettingsFormLabelContainer>
                                    <SettingsFormLabelLeftContainer>Tag Color</SettingsFormLabelLeftContainer>
                                    <SettingsFormLabelRightContainer><IconHelp /></SettingsFormLabelRightContainer>
                                </SettingsFormLabelContainer>
                                <CookieColorsContainer>
                                    <CookieColorsRow>
                                        <p style={{flex: '0 0 91px'}}>Background</p>
                                        <CookieColorPickerWrapper>
                                            <FastField
                                            name="color.wrapper.background"
                                            render={({ field }: any) => (
                                            <CookieColorPickerContent className='w-100px'>
                                                <ColorPicker
                                                color={field.value}
                                                onChange={(color) => setFieldValue('color.wrapper.background', color ) }
                                                />
                                            </CookieColorPickerContent>
                                            )} />
                                        </CookieColorPickerWrapper>
                                    </CookieColorsRow>
                                </CookieColorsContainer>
                                <CookieColorsContainer>
                                    <CookieColorsRow>
                                        <p style={{flex: '0 0 91px'}}>Text</p>
                                        <CookieColorPickerWrapper>
                                        <FastField
                                            name="color.wrapper.text"
                                            render={({ field }: any) => (
                                            <CookieColorPickerContent className='w-100px'>
                                                <ColorPicker
                                                color={field.value}
                                                onChange={(color) => setFieldValue('color.wrapper.text', color ) }
                                                />
                                            </CookieColorPickerContent>
                                            )} />
                                        </CookieColorPickerWrapper>
                                    </CookieColorsRow>
                                </CookieColorsContainer>
                            </SettingsFormGroupInput>
                            <SettingsFormGroupInput style={{gap: 16, paddingBottom: 16}}>
                                <SettingsFormLabelContainer>
                                    <SettingsFormLabelLeftContainer>Icon Color</SettingsFormLabelLeftContainer>
                                    <SettingsFormLabelRightContainer><IconHelp /></SettingsFormLabelRightContainer>
                                </SettingsFormLabelContainer>
                                <CookieColorsContainer>
                                    <CookieColorsRow>
                                        <p style={{flex: '0 0 91px'}}>Background</p>
                                        <CookieColorPickerWrapper>
                                        <FastField
                                            name="color.icon.background"
                                            render={({ field }: any) => (
                                            <CookieColorPickerContent className='w-100px'>
                                                <ColorPicker
                                                color={field.value}
                                                onChange={(color) => setFieldValue('color.icon.background', color ) }
                                                />
                                            </CookieColorPickerContent>
                                            )} />
                                        </CookieColorPickerWrapper>
                                    </CookieColorsRow>
                                </CookieColorsContainer>
                                <CookieColorsContainer>
                                    <CookieColorsRow>
                                        <p style={{flex: '0 0 91px'}}>Text / Icon</p>
                                        <CookieColorPickerWrapper>
                                        <FastField
                                            name="color.icon.text"
                                            render={({ field }: any) => (
                                            <CookieColorPickerContent className='w-100px'>
                                                <ColorPicker
                                                color={field.value}
                                                onChange={(color) => setFieldValue('color.icon.text', color ) }
                                                />
                                            </CookieColorPickerContent>
                                            )} />
                                        </CookieColorPickerWrapper>
                                    </CookieColorsRow>
                                </CookieColorsContainer>
                            </SettingsFormGroupInput>
                            </>}
                            {tab == 'add' && <>
                            <SettingsFormGroupInput style={{gap: 16}}>
                                <EligibleItemsContainer className='menus-v2'>
                                    <FormTreeSelect
                                        id={`add-to-dishes`}
                                        selected={add_to}
                                        nodes={isV2 ? (RestaurantUtils.menu as any).getTreeFilterV2(restaurant) : RestaurantUtils.menu.getTreeFilter(restaurant, () => true)}
                                        onChange={(add) => {
                                            setAddTo(add)
                                        }}
                                    />
                                </EligibleItemsContainer>
                            </SettingsFormGroupInput>
                            </>}
                        </SettingsFormGroup>
                        <SettingsActionButtonContainer>
                            <SettingsActionButtonWrapper style={{left: 0}}>
                                <button type="submit" disabled={isSubmitting}>
									{isSubmitting && <RotateLoader size={2} color="white" />}
                                    {!isSubmitting && "Save"}
                                </button>
                            </SettingsActionButtonWrapper>
                        </SettingsActionButtonContainer>
                        </>)
                    }}

              </RestaurantForm>
    </div>)
}
